import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { Spinner } from '@/app/components/ui/Spinner';
import { apiGet } from '@/api/client';
import type { Me } from '@/api/types';
import { derivePermissions } from '@/features/me/permissions';
import type { Permissions } from '@/features/me/permissions';
import i18n from '@/i18n';
import { hasManualLanguageChoice, isSupportedLanguage } from '@/i18n/languagePreference';
import { useAsync } from '@/lib/useAsync';

export interface MeContextValue {
  me: Me;
  permissions: Permissions;
  /** Re-fetch after a write that changes memberships, roles or consents. */
  refresh: () => void;
}

const MeContext = createContext<MeContextValue | null>(null);

/**
 * Fetches `/me` once the session exists and holds it for the whole app: the
 * caller's identity, memberships and grants are what every page renders from.
 * Children render only after the payload is in, so `useMe()` never has a
 * loading state to handle.
 */
export function MeProvider({ children }: { children: ReactNode }) {
  const state = useAsync(async () => (await apiGet('/me')) as unknown as Me, []);

  // Profile language wins over the browser guess, but never over a language
  // the visitor picked explicitly (in this session or a previous one) — see
  // the comment on `hasManualLanguageChoice` for why that isn't simply
  // "localStorage.i18nextLng is empty".
  const preferredLanguage = state.data?.user?.preferred_language;
  useEffect(() => {
    if (
      isSupportedLanguage(preferredLanguage) &&
      !hasManualLanguageChoice() &&
      preferredLanguage !== i18n.language
    ) {
      void i18n.changeLanguage(preferredLanguage);
    }
  }, [preferredLanguage]);

  const value = useMemo<MeContextValue | null>(
    () =>
      state.data
        ? {
            me: state.data,
            permissions: derivePermissions(state.data.roles ?? []),
            refresh: state.reload,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is stable per fetch
    [state.data],
  );

  if (state.error) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- this only runs once
    const { t } = useTranslation();
    return (
      <div className="bg-paper flex min-h-screen items-center justify-center p-4">
        <Card className="flex w-full max-w-sm flex-col items-center gap-4">
          <p className="text-accent text-sm">NOLATR</p>
          <h1 className="text-ink text-center text-xl font-semibold">
            {t('me.loadingFailed', 'Could not load your profile')}
          </h1>
          <p className="text-ink text-sm">{state.error.message}</p>
          <Button onClick={state.reload} size="sm">
            {t('ui.tryAgain', 'Try again')}
          </Button>
        </Card>
      </div>
    );
  }

  if (!value) {
    return (
      <div className="bg-paper flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <p className="sr-only">Loading your profile</p>
          <Spinner className="size-8" />
        </div>
      </div>
    );
  }

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider
export function useMe(): MeContextValue {
  const value = useContext(MeContext);
  if (!value) {
    throw new Error('useMe() requires a MeProvider above it');
  }
  return value;
}

/** Test seam: render a subtree against a fixed `/me` without fetching. */
export function MeContextProvider({
  value,
  children,
}: {
  value: MeContextValue;
  children: ReactNode;
}) {
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}
